import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose, setPropTypes, lifecycle, withState } from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import ReactSelectMaterialUi from 'react-select-material-ui';
import BackButton from '../../components/atoms/BackButton';
import RegisterButton from '../../components/atoms/RegisterButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import ImagePreview from '../../components/molecules/ImagePreview';
import { languages, languageType } from '../../constants/Language';

const styles = {
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none'
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px'
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  description: {
    color: '#707070',
    margin: '20px 0px 0px'
  },
  subTitle: {
    margin: '20px 0px 0px',
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  divider: {
    margin: '20px 0px'
  },
  detailTable: {
    width: 'auto'
  },
  tableCellItem: {
    border: '2px solid #ffffff',
    backgroundColor: '#e0e0e0'
  },
  tableCellData: {
    border: '2px solid #ffffff'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px'
  },
  editTable: {
    width: 'auto'
  },
  tableCell: {
    border: '2px solid #ffffff'
  },
  selectContainer: {
    marginLeft: 40,
    marginRight: 40
  },
  selectControl: {
    width: '100%'
  },
  breakLines: {
    whiteSpace: 'break-spaces'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(state => ({
    name: state.loginInfo.name,
    prefectures: state.commons.prefectures
  })),
  setPropTypes(propTypes),
  withState('isLanguage', 'setIsLanguage', 'ja'),
  lifecycle({
    componentDidMount() {
      this.props.setIsLanguage(this.props.currentLanguage);
    }
  })
);

const Main = enhance(
  ({
    classes,
    backAction,
    editAction,
    editParam,
    openEditProgress,
    progressOkAction,
    loading,
    resultMessage,
    prefectures,
    isLanguage,
    setIsLanguage,
    otherData
  }) => {
    const editConfirm = { ...editParam, ...otherData };
    if (isLanguage !== languageType.ORIGIN) {
      const languageData = editParam.otherLanguages.find(
        language => language.language === isLanguage
      );
      if (languageData && languageData.translateType) {
        Object.assign(editConfirm, languageData.params);
      } else {
        Object.assign(editConfirm, {
          groupName: 'Google Translate で自動的に翻訳されるもの',
          groupContactName: 'Google Translate で自動的に翻訳されるもの',
          department: 'Google Translate で自動的に翻訳されるもの',
          description: 'Google Translate で自動的に翻訳されるもの'
        });
      }
      Object.assign(editConfirm, {
        address: 'Google Translate で自動的に翻訳されるもの',
        building: 'Google Translate で自動的に翻訳されるもの'
      });
    }

    const groupDisplayLimit =
      editConfirm.groupType === 'CAMPAIGN' ||
      editConfirm.groupType === 'OTHERS' ||
      editConfirm.groupType === 'GOCHI_KID';

    return (
      <div className={classes.root}>
        <Typography variant="h6">グループ運営会社編集確認</Typography>
        <div className={classes.selectContainer}>
          <ReactSelectMaterialUi
            required
            className={[classes.textField, classes.selectControl]}
            defaultValue={isLanguage}
            value={isLanguage}
            options={languages}
            margin="normal"
            onChange={v => {
              if (isLanguage === v) {
                return;
              }
              setIsLanguage(v);
            }}
          />
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <Table className={classes.detailTable}>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                運営会社名
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.groupName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>郵便番号</TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.zipCode}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>都道府県</TableCell>
              <TableCell className={classes.tableCellData}>
                {prefectures &&
                  editConfirm.prefectureCode &&
                  prefectures.map(item => {
                    if (
                      editConfirm.prefectureCode.toString() ===
                      item.id.toString()
                    ) {
                      return item.name;
                    }
                    return '';
                  })}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>市区町村</TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.city}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                町域・番地
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.address}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>建物名</TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.building}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>電話番号</TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.phone}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>FAX</TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.fax}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                ご担当者名
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.groupContactName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>所属部署</TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.department}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                電話番号（直通等）
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.directPhone}
              </TableCell>
            </TableRow>
            {!groupDisplayLimit && (
              <>
                <TableRow>
                  <TableCell className={classes.tableCellItem}>
                    インボイス制度 適格事業者登録
                  </TableCell>
                  <TableCell className={classes.tableCellData}>
                    {editConfirm.registrationId ? '登録済み' : '未登録'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.tableCellItem}>
                    適格事業者登録番号
                  </TableCell>
                  <TableCell className={classes.tableCellData}>
                    {editConfirm.registrationId}
                  </TableCell>
                </TableRow>
              </>
            )}
            <TableRow>
              <TableCell className={classes.tableCellItem}>画像</TableCell>
              <TableCell className={classes.tableCellData}>
                <ImagePreview previewImage={editConfirm.previewImage1} />
                <ImagePreview previewImage={editConfirm.previewImage2} />
                <ImagePreview previewImage={editConfirm.previewImage3} />
                <ImagePreview previewImage={editConfirm.previewImage4} />
                <ImagePreview previewImage={editConfirm.previewImage5} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループの概要
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.description}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                WebページURL
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.website}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループ種別
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.groupTypeStr}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループ紐付け時のメッセージ
              </TableCell>
              <TableCell
                className={`${classes.tableCellData} ${classes.breakLines}`}
              >
                {editConfirm.linkingShopMessage}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                メールアドレス
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {editConfirm.email}
              </TableCell>
            </TableRow>
            {!groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  入金管理
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.depositManageStr}
                </TableCell>
              </TableRow>
            )}
            {/* {editConfirm.groupType !== 'OTHERS' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  社食ごちめし現金授受の有無
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.groupDetail &&
                  editConfirm.groupDetail.cashAllowed
                    ? '受け付ける'
                    : '受け付けない'}
                </TableCell>
              </TableRow>
            )} */}
            {editConfirm.groupType !== 'OTHERS' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  社食ごちめし金額指定チケット利用の有無
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.groupDetail &&
                  editConfirm.groupDetail.allowMenuPriceShashoku
                    ? '利用する'
                    : '利用しない'}
                </TableCell>
              </TableRow>
            )}
            {editParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>銀行名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankName}
                </TableCell>
              </TableRow>
            )}
            {editParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>支店名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankBranchName}
                </TableCell>
              </TableRow>
            )}
            {editParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座種別
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankAccountTypeStr}
                </TableCell>
              </TableRow>
            )}
            {editParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座番号
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankAccountNumber}
                </TableCell>
              </TableRow>
            )}
            {editParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座名義
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {editConfirm.bankAccountHolder}
                </TableCell>
              </TableRow>
            )}
          </Table>
        </Paper>
        <div>
          <BackButton onClickAction={backAction} />
          <RegisterButton onClickAction={editAction} />
        </div>
        <ProgressDialog
          openProgress={openEditProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
